import { type Location, type LocationDescriptor } from "history";
import { generatePath } from "react-router";

import {
    getPreviousPathname,
    getPreviousTitle,
    isPreviousLocation,
} from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation";
import {
    type IPreviousLocation,
    type IRouteLocationState,
} from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { addPathForCurrentPortal } from "utilities/routes";

// The ParamsType type is a utility type used for route params and query params.
type ParamsType<T> = T extends undefined ? void : T;
interface IRouteOptions {
    /**
     * If true, the previous location state will be used when generating the link. If false, the previous location state will not be used.
     * @default true
     */
    usePrevious?: boolean;
    /**
     * If previous location props are provided, it will override the automatic previous location that is generated by the createRoute function.
     */
    overridePrevious?: Partial<IPreviousLocation>;

    /**
     * Previous Page state. This should be used for things like filters, query parameters, etc. So when returning to the previous page, the previous page state can be restored.
     */
    pageState?: Record<string, unknown>;
}

export interface IRoute<T, Q> {
    /**
     * The path of the route.
     */
    path: string;

    /**
     * The user-friendly title of the route. This is used for display in the UI.
     */
    title: string;

    /**
     * A function that generates a link to the route with the given parameters and query parameters.
     * @param params The parameters for the route.
     * @param queryParams The query parameters for the route.
     * @param options Options for generating the link.
     * @returns The link to the route. Can be a string or a RouteLocationState object default is a RouteLocationState object, which includes the previous location state.
     */
    link: (
        params: ParamsType<T>,
        queryParams?: ParamsType<Q>,
        options?: IRouteOptions,
    ) => string | IRouteLocationState;
}

// This is a mapping of route names to clean titles. This is used to clean up the title of a route for display in the UI since some of our route names do not match there user-friendly titles.
const ROUTE_TO_USER_FRIENDLY_TITLES: Record<string, string> = {
    BidPackages: "Bids",
    "Bill-review-queue": "bill review queue",
    DepositsList: "Deposits",
    Landing: "Summary",
    LeadPage: "Lead Opportunity",
    OwnerInvoices: "Invoices",
    Purchasing: "Cashback & Discounts",
    Receipts: "Cost Inbox",
    Schedules: "Schedule",
    ToDos: "To-Dos",
    RFIs: "RFIs",
};

/**
 * createRoute is a utility function that creates a route object with a title, path, and link function. This function is expected to be used with flat routing.
 *
 * @param path the path of the route
 * @param title the title of the route used for navigation/user interface
 * @param defaultParams the default parameters for the route
 * @returns an object with a title, path, and link function
 */
export function createRoute<T = undefined, Q = undefined>(
    path: string,
    title: string,
    defaultParams?: Partial<T>,
): IRoute<T, Q> {
    return {
        title,
        path,
        link: (params: ParamsType<T>, queryParams?: ParamsType<Q>, options?: IRouteOptions) => {
            const finalParams = { ...defaultParams, ...params };
            const generatedPath = generatePath(path, finalParams ?? undefined);
            let finalPath = generatedPath;
            if (queryParams) {
                finalPath = addQueryParams(generatedPath, queryParams);
            }
            finalPath = addPathForCurrentPortal(finalPath);
            const usePreviousState = options?.usePrevious ?? options?.overridePrevious ?? true;

            if (usePreviousState) {
                const routeLocationState = createRouteLocationState(
                    finalPath,
                    options?.pageState,
                    options?.overridePrevious,
                );
                return routeLocationState;
            } else {
                return finalPath;
            }
        },
    };
}

/**
 * Adds the portal paths to a path, so that the path is accessible from all portals.
 * @param path the path to add the portal paths to
 * @returns the path with the portal paths added
 */
export function getPathForAllPortals(path: string) {
    return `(/Admin)?(/Owner)?(/Subs)?${path}`;
}

/**
 * Determines if a route is a RouteLocationState object. This is used to determine if a route is a RouteLocationState object or a string path.
 * @param route the route to check if it is a RouteLocationState object
 * @returns true if the route is a RouteLocationState object, false otherwise
 */
export function isRouteLocationState(
    route:
        | LocationDescriptor<unknown>
        | ((location: Location<unknown>) => LocationDescriptor<unknown>),
): route is IRouteLocationState {
    return isPreviousLocation(route) && "previous" in (route as IRouteLocationState).state;
}

/**
 * Gets the title that should be used for the previous route.
 * This is used when navigating to a new route and allows the previous route title to be passed along so that we can return to the previous route with a back button/link.
 * @returns the title that should be used for the previous route
 */
function getPreviousRouteTitle() {
    let prevTitle = getPreviousTitle();
    prevTitle =
        ROUTE_TO_USER_FRIENDLY_TITLES[prevTitle] ?? prevTitle.replace(/([A-Z])/g, " $1").trim();

    return prevTitle;
}

/**
 * Creates a RouteLocationState object with a previous location state.
 * This is used when navigating to a new route and allows the previous location state to be passed along so that we can return to the previous route with a back button/link.
 * @param path the path to create the RouteLocationState object with
 * @param previousLocation the previous location state to add to the RouteLocationState object
 * @returns a RouteLocationState object with the previous location state
 */
function createRouteLocationState(
    path: string,
    pageState?: Record<string, unknown>,
    previousLocation?: Partial<IPreviousLocation>,
): IRouteLocationState {
    return {
        pathname: path,
        state: {
            previous: {
                title: previousLocation?.title ?? getPreviousRouteTitle(),
                pathname: previousLocation?.pathname ?? getPreviousPathname(),
                pageState: pageState,
            },
        },
    };
}

/**
 * Adds query parameters to a path.
 * @param path a path to add query parameters to that currently does not have any
 * @param searchParams a record of key-value pairs to add as query parameters
 * @returns the path with the query parameters added
 */
function addQueryParams(path: string, searchParams: Record<string, string>) {
    const urlParams = new URLSearchParams(searchParams);
    return `${path}?${urlParams.toString()}`;
}
